import CloseIcon from '@mui/icons-material/Close'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Portal,
  TextField,
  Typography,
  Tabs,
  Tab,
  Grid2,
  Chip,
  Fade,
  Backdrop,
} from '@mui/material'
import { useEffect, useState, useRef } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import ReCAPTCHA from 'react-google-recaptcha'
import { toast } from 'react-toastify'
import { getCPDataWithParams, postCPDataWithoutParams } from 'src/clinic/services'
import { formatPhoneNumber } from 'src/consumer/helpers/utilFunctions'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'

const CLINIC_DESK_REFRESH_INTERVAL_MS = 2 * 60 * 1000
const CLINIC_DESK_LOCAL_TICK_INTERVAL_MS = 30 * 1000

function parseRosterInstant(value) {
  if (!value) {
    return null
  }
  const timestamp = Date.parse(value)
  return Number.isFinite(timestamp) ? timestamp : null
}

function getClinicDeskCandidateKey(practitioner) {
  if (!practitioner) {
    return null
  }
  return [
    practitioner.id || 'unknown',
    practitioner.rosterStartAt || 'na',
    practitioner.rosterEndAt || 'na',
    practitioner.rosterStatus || 'na',
  ].join('::')
}

function dedupeClinicDeskCandidates(practitioners) {
  const seen = new Set()
  return practitioners.filter((practitioner) => {
    const key = getClinicDeskCandidateKey(practitioner)
    if (!key || seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

function getCandidateStatusAt(practitioner, nowMs) {
  if (!practitioner) {
    return 'unavailable'
  }
  const startMs = parseRosterInstant(practitioner.rosterStartAt)
  const endMs = parseRosterInstant(practitioner.rosterEndAt)
  if (startMs !== null && endMs !== null && nowMs >= startMs && nowMs < endMs) {
    return 'current'
  }
  if (startMs !== null && nowMs < startMs) {
    return 'upcoming'
  }
  if (practitioner.rosterStatus === 'current' || practitioner.rosterStatus === 'upcoming') {
    return practitioner.rosterStatus
  }
  return practitioner.checkedIn ? 'current' : 'unavailable'
}

function buildClinicDeskHelperText({
  title,
  practitioner,
  status,
  roleData,
}) {
  if (!practitioner) {
    return `No ${title.toLowerCase()} rostered right now`
  }

  if (status === 'current') {
    const additionalCurrentCount = Math.max((roleData?.currentCount || 0) - 1, 0)
    const additionalLabel =
      additionalCurrentCount > 0
        ? ` ${additionalCurrentCount} more rostered now.`
        : ''
    if (practitioner.checkedIn) {
      return `Checked in at clinic.${additionalLabel}`.trim()
    }
    return `${practitioner.rosterDetailLabel || 'Rostered now'}${additionalLabel}`.trim()
  }

  if (status === 'upcoming') {
    const additionalUpcomingCount = Math.max((roleData?.upcomingCount || 0) - 1, 0)
    const additionalLabel =
      additionalUpcomingCount > 0
        ? ` ${additionalUpcomingCount} more upcoming.`
        : ''
    return `${practitioner.rosterDetailLabel || 'Next roster slot scheduled'}${additionalLabel}`.trim()
  }

  return `No ${title.toLowerCase()} rostered right now`
}

function getClinicDeskHeadline(title, status, practitioner) {
  if (status === 'current') {
    return practitioner?.name || `${title} available now`
  }
  return 'Unavailable right now'
}

function resolveClinicDeskRoleView(roleData, fallbackPractitioner, nowMs, title) {
  const candidates = dedupeClinicDeskCandidates(
    [
      roleData?.currentPractitioner,
      roleData?.actionPractitioner,
      roleData?.displayPractitioner,
      roleData?.upcomingPractitioner,
      fallbackPractitioner,
    ].filter(Boolean),
  )

  const currentPractitioner =
    candidates.find((candidate) => getCandidateStatusAt(candidate, nowMs) === 'current') || null

  const upcomingPractitioner =
    candidates
      .filter((candidate) => getCandidateStatusAt(candidate, nowMs) === 'upcoming')
      .sort((left, right) => {
        const leftStart = parseRosterInstant(left?.rosterStartAt) || Number.MAX_SAFE_INTEGER
        const rightStart = parseRosterInstant(right?.rosterStartAt) || Number.MAX_SAFE_INTEGER
        return leftStart - rightStart
      })[0] || null

  const actionCandidate =
    roleData?.actionPractitioner && getCandidateStatusAt(roleData.actionPractitioner, nowMs) === 'current'
      ? roleData.actionPractitioner
      : currentPractitioner

  if (actionCandidate) {
    return {
      practitioner: currentPractitioner || actionCandidate,
      actionPractitioner: actionCandidate,
      status: 'current',
      headline: getClinicDeskHeadline(title, 'current', currentPractitioner || actionCandidate),
      statusLabel: 'Rostered now',
      statusTone: 'success',
      helperText: buildClinicDeskHelperText({
        title,
        practitioner: currentPractitioner || actionCandidate,
        status: 'current',
        roleData,
      }),
      timeLabel: (currentPractitioner || actionCandidate)?.rosterTimeLabel,
      actionEnabled: true,
      showIdentity: true,
      showButton: true,
      buttonLabel: `Call ${title}`,
    }
  }

  if (upcomingPractitioner) {
    return {
      practitioner: null,
      actionPractitioner: null,
      status: 'upcoming',
      headline: getClinicDeskHeadline(title, 'upcoming', null),
      statusLabel: 'Next slot today',
      statusTone: 'info',
      helperText: 'Next rostered slot today (local time)',
      timeLabel: upcomingPractitioner?.rosterTimeLabel,
      actionEnabled: false,
      showIdentity: false,
      showButton: false,
      buttonLabel: `Call ${title}`,
    }
  }

  return {
    practitioner: null,
    actionPractitioner: null,
    status: 'unavailable',
    headline: getClinicDeskHeadline(title, 'unavailable', null),
    statusLabel: 'Unavailable',
    statusTone: 'muted',
    helperText: 'No rostered slot later today',
    timeLabel: null,
    actionEnabled: false,
    showIdentity: false,
    showButton: false,
    buttonLabel: `Call ${title}`,
  }
}

// Video Thumbnail Card Component
function VideoThumbnailCard({ video, onClick }) {
  const thumbnailUrl = `https://img.youtube.com/vi/${video?.contentId}/maxresdefault.jpg`
  
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        border: '1px solid rgba(67, 56, 202, 0.15)',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: 'white',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(67, 56, 202, 0.2)',
          borderColor: 'rgba(67, 56, 202, 0.3)',
        },
      }}
    >
      {/* Thumbnail */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          paddingTop: '56.25%', // 16:9 aspect ratio
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={thumbnailUrl}
          alt={video?.name || 'Video thumbnail'}
          onError={(e) => {
            // Fallback to a lower quality thumbnail if maxresdefault fails
            e.target.src = `https://img.youtube.com/vi/${video?.contentId}/hqdefault.jpg`
          }}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
            },
          }}
        />
        {/* Play Button Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translate(-50%, -50%) scale(1.1)',
              backgroundColor: 'white',
              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
            },
          }}
        >
          <PlayCircleOutlineIcon
            sx={{
              fontSize: 40,
              color: 'rgba(67, 56, 202, 0.9)',
              marginLeft: '4px', // Slight offset for visual centering
            }}
          />
        </Box>
        {/* Duration Badge (if available) */}
        {video?.duration && (
          <Chip
            label={video.duration}
            size="small"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              color: 'white',
              fontSize: '11px',
              fontWeight: 600,
              height: 20,
              '& .MuiChip-label': {
                padding: '0 6px',
              },
            }}
          />
        )}
      </Box>

      {/* Video Info */}
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: 'rgba(15, 23, 42, 0.95)',
            fontSize: '15px',
            lineHeight: 1.4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minHeight: '42px',
          }}
        >
          {video?.name || 'Untitled Video'}
        </Typography>
        {video?.description && (
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: 'rgba(100, 116, 139, 0.7)',
              fontSize: '13px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {video.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

function ClinicDeskPractitionerCard({
  title,
  description,
  practitioner,
  buttonLabel,
  onClick,
  busy,
  statusLabel,
  statusTone = 'muted',
  helperText,
  timeLabel,
  actionEnabled = true,
  showIdentity = true,
  showButton = true,
  headline,
}) {
  const imageUrl = getImageLink(practitioner?.profilePicId, null, false)
  const statusStyles = {
    success: {
      backgroundColor: 'rgba(16, 185, 129, 0.12)',
      color: '#059669',
    },
    info: {
      backgroundColor: 'rgba(59, 130, 246, 0.12)',
      color: '#2563eb',
    },
    muted: {
      backgroundColor: 'rgba(148, 163, 184, 0.16)',
      color: 'rgba(71, 85, 105, 0.88)',
    },
  }
  const selectedStatusStyle = statusStyles[statusTone] || statusStyles.muted

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid rgba(67, 56, 202, 0.16)',
        borderRadius: 3,
        overflow: 'hidden',
        background:
          'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 246, 255, 0.98) 100%)',
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Typography
          variant="overline"
          sx={{
            color: 'rgba(67, 56, 202, 0.72)',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
          {showIdentity && practitioner ? (
            <Avatar
              src={imageUrl || undefined}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'rgba(67, 56, 202, 0.12)',
                color: 'rgba(67, 56, 202, 0.88)',
              }}
            >
              {practitioner?.name?.[0] || title?.[0] || '?'}
            </Avatar>
          ) : (
            <Box
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                borderRadius: '50%',
                backgroundColor: 'rgba(148, 163, 184, 0.10)',
                border: '1px dashed rgba(148, 163, 184, 0.35)',
              }}
            />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'rgba(15, 23, 42, 0.95)',
                fontSize: '17px',
                lineHeight: 1.25,
              }}
            >
              {headline || practitioner?.name || `No ${title?.toLowerCase()} rostered`}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: 'rgba(71, 85, 105, 0.78)',
                fontSize: '13px',
              }}
            >
              {helperText || description}
            </Typography>
            {!!timeLabel && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: 'rgba(67, 56, 202, 0.78)',
                  fontSize: '12px',
                  fontWeight: 700,
                }}
              >
                {timeLabel}
              </Typography>
            )}
          </Box>
          {!!statusLabel && (
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                ...selectedStatusStyle,
                fontWeight: 700,
              }}
            />
          )}
        </Box>
        {showButton && (
          <Button
            fullWidth
            variant="contained"
            onClick={onClick}
            disabled={!practitioner || busy || !actionEnabled}
            sx={{
              mt: 2,
              py: 1.15,
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              background:
                'linear-gradient(135deg, rgba(79, 70, 229, 0.95) 0%, rgba(59, 130, 246, 0.95) 100%)',
            }}
          >
            {busy ? <CircularProgress size={18} sx={{ color: 'white' }} /> : buttonLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

export default function YoutubeHelper({
  fetchUrl = '/api/secure/teacher/doc-side-content',
  buttonTopOffset = 0,
  flowId,
  contextId,
}) {
  const location = useLocation()
  const clinicManageBookingsMatch = matchPath(
    {
      path: '/ca-home/:clinicBrandId/:clinicId/manage-bookings/*',
      end: false,
    },
    location.pathname,
  )
  const clinicLocationId = clinicManageBookingsMatch?.params?.clinicId
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [hasAttemptedContentLoad, setHasAttemptedContentLoad] = useState(false)
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [isLoadingClinicDesk, setIsLoadingClinicDesk] = useState(false)
  const [playlistLinks, setPlaylistLinks] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [supportNumber, setSupportNumber] = useState(null)
  const [supportWhatsappLink, setSupportWhatsappLink] = useState(null)
  const [clinicDeskData, setClinicDeskData] = useState(null)
  const [clinicDeskNowMs, setClinicDeskNowMs] = useState(() => Date.now())
  const [clinicDeskClockOffsetMs, setClinicDeskClockOffsetMs] = useState(0)
  const [pendingAction, setPendingAction] = useState(null)
  const [submittingAction, setSubmittingAction] = useState(false)
  const [captchaAnswer, setCaptchaAnswer] = useState('')
  const [captchaChallenge, setCaptchaChallenge] = useState(null)
  const [recaptchaToken, setRecaptchaToken] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [bulletinContent, setBulletinContent] = useState([]) // Will be populated via API later

  // Check if we're in CA Portal
  const isCAPortal = location.pathname.startsWith('/ca-home') || location.pathname.startsWith('/kiosk')
  const showClinicDeskTab =
    location.pathname.startsWith('/ca-home') &&
    location.pathname.includes('/manage-bookings') &&
    Boolean(clinicLocationId)
  const clinicDeskTabIndex = showClinicDeskTab ? 0 : -1
  const bulletinTabIndex = showClinicDeskTab ? 1 : 0
  const helpVideosTabIndex = showClinicDeskTab ? 2 : 1
  const canShowDocSideButton = Boolean(flowId || contextId || showClinicDeskTab)
  const recaptchaRef = useRef(null)
  const hasLoadedClinicDeskRef = useRef(false)

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true)

  function createFallbackCaptchaChallenge() {
    const first = Math.floor(Math.random() * 7) + 2
    const second = Math.floor(Math.random() * 6) + 3
    return {
      prompt: `${first} + ${second}`,
      answer: String(first + second),
    }
  }

  function getResponseMessage(resp, fallbackMessage) {
    const possibleMessage = resp?.data?.message || resp?.response?.data?.message
    if (typeof possibleMessage === 'string' && possibleMessage.trim()) {
      return possibleMessage
    }
    if (Array.isArray(possibleMessage) && possibleMessage.length > 0) {
      return possibleMessage[0]
    }
    if (possibleMessage?.message) {
      return possibleMessage.message
    }
    return fallbackMessage
  }

  function resetActionDialog() {
    setPendingAction(null)
    setSubmittingAction(false)
    setCaptchaAnswer('')
    setRecaptchaToken('')
    setCaptchaChallenge(createFallbackCaptchaChallenge())
    recaptchaRef.current?.reset?.()
  }

  function openActionDialog(actionType, practitioner = null) {
    setPendingAction({ actionType, practitioner })
    setSubmittingAction(false)
    setCaptchaAnswer('')
    setRecaptchaToken('')
    setCaptchaChallenge(createFallbackCaptchaChallenge())
    recaptchaRef.current?.reset?.()
  }

  //sample object for playlistLinks
  // {
  //   "name": "Help Videos",
  //   "type": "playlist",
  //   "link": "https://www.youtube.com/watch?v=dQw4w9WgXcQ"
  // }

  useEffect(() => {
    setDrawerOpen(false)
    setModalOpen(false)
    setSelectedVideo(null)
    setHasAttemptedContentLoad(false)
    setIsLoadingContent(false)
    setIsLoadingClinicDesk(false)
    setPlaylistLinks([])
    setSupportNumber(null)
    setSupportWhatsappLink(null)
    setClinicDeskData(null)
    setClinicDeskNowMs(Date.now())
    setClinicDeskClockOffsetMs(0)
    setBulletinContent([])
    hasLoadedClinicDeskRef.current = false
    resetActionDialog()
  }, [fetchUrl, flowId, contextId])

  useEffect(() => {
    setActiveTab(0)
    setClinicDeskData(null)
    setClinicDeskNowMs(Date.now())
    setClinicDeskClockOffsetMs(0)
    hasLoadedClinicDeskRef.current = false
    resetActionDialog()
  }, [location.pathname, clinicLocationId])

  useEffect(() => {
    if (!hasAttemptedContentLoad) {
      return
    }

    // Check token validity before making API call
    const token = localStorage.getItem('token')
    if (isTokenInvalidOrAbsent(token)) {
      // Don't make API call if token is invalid
      return
    }

    let isCancelled = false

    async function fetchPlaylists() {
      // Double-check token before making the request
      const currentToken = localStorage.getItem('token')
      if (isTokenInvalidOrAbsent(currentToken) || isCancelled) {
        return
      }

      try {
        if (isMountedRef.current) {
          setIsLoadingContent(true)
        }
        const resp = await getMPDataWithParams(fetchUrl, {
          flowId,
          'context-id': contextId,
        })
        
        // Check if component is still mounted and not cancelled
        if (isCancelled || !isMountedRef.current) {
          return
        }

        // Check token again after response
        const tokenAfterResponse = localStorage.getItem('token')
        if (isTokenInvalidOrAbsent(tokenAfterResponse)) {
          return
        }

        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          let ytLinks = data?.['playable_external_content'] || []
          setPlaylistLinks(ytLinks)
          setSupportNumber(data?.['humanSupportNumber'])
          setSupportWhatsappLink(data?.['supportWhatsappLink'])
          // if (links.length > 0) {
          // setSelectedPlaylist(links[0])
          // }
        } else {
          console.error('Failed to load playlists:', resp)
        }
      } catch (err) {
        // Only log error if component is still mounted and not cancelled
        if (!isCancelled && isMountedRef.current) {
          // Don't log 401 errors as they're expected after logout
          if (err?.response?.status !== 401) {
            console.error('Failed to load playlists:', err)
          }
        }
      } finally {
        if (!isCancelled && isMountedRef.current) {
          setIsLoadingContent(false)
        }
      }
    }

    fetchPlaylists()

    // Cleanup function to cancel the request
    return () => {
      isCancelled = true
    }
  }, [contextId, fetchUrl, flowId, hasAttemptedContentLoad])

  useEffect(() => {
    if (!drawerOpen || !hasAttemptedContentLoad || !showClinicDeskTab || !clinicLocationId) {
      return
    }

    const token = localStorage.getItem('token')
    if (isTokenInvalidOrAbsent(token)) {
      return
    }

    let isCancelled = false

    async function fetchClinicDeskData() {
      const shouldShowLoading = !hasLoadedClinicDeskRef.current
      try {
        if (isMountedRef.current && shouldShowLoading) {
          setIsLoadingClinicDesk(true)
        }
        const resp = await getCPDataWithParams('/api/secure/ca/doc-side/clinic-desk', {
          clinicLocationId,
        })

        if (isCancelled || !isMountedRef.current) {
          return
        }

        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const data = getDataMapFromCommonResponse(resp)
          setClinicDeskData(data)
          hasLoadedClinicDeskRef.current = true
          setClinicDeskNowMs(Date.now())
          const serverNowMs = parseRosterInstant(data?.serverNow)
          setClinicDeskClockOffsetMs(
            serverNowMs !== null ? serverNowMs - Date.now() : 0,
          )
          if (data?.humanSupportNumber) {
            setSupportNumber(data.humanSupportNumber)
          }
        } else if (!isCancelled) {
          console.error('Failed to load clinic desk data:', resp)
        }
      } catch (err) {
        if (!isCancelled && isMountedRef.current && err?.response?.status !== 401) {
          console.error('Failed to load clinic desk data:', err)
        }
      } finally {
        if (!isCancelled && isMountedRef.current && shouldShowLoading) {
          setIsLoadingClinicDesk(false)
        }
      }
    }

    fetchClinicDeskData()
    const refreshInterval = window.setInterval(fetchClinicDeskData, CLINIC_DESK_REFRESH_INTERVAL_MS)

    return () => {
      isCancelled = true
      window.clearInterval(refreshInterval)
    }
  }, [drawerOpen, hasAttemptedContentLoad, showClinicDeskTab, clinicLocationId])

  useEffect(() => {
    if (!drawerOpen || !showClinicDeskTab) {
      return
    }

    const tickInterval = window.setInterval(() => {
      setClinicDeskNowMs(Date.now())
    }, CLINIC_DESK_LOCAL_TICK_INTERVAL_MS)

    return () => {
      window.clearInterval(tickInterval)
    }
  }, [drawerOpen, showClinicDeskTab])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Fetch bulletin content when API is ready
  // useEffect(() => {
  //   async function fetchBulletinContent() {
  //     try {
  //       const resp = await getMPDataWithParams('/api/secure/teacher/doc-side-bulletin', {
  //         flowId,
  //         'context-id': contextId,
  //       })
  //       if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
  //         const data = getDataMapFromCommonResponse(resp)
  //         setBulletinContent(data?.bulletins || [])
  //       }
  //     } catch (err) {
  //       console.error('Failed to load bulletin content:', err)
  //     }
  //   }
  //   fetchBulletinContent()
  // }, [fetchUrl, flowId, contextId])

  const resolvedSupportNumber = clinicDeskData?.humanSupportNumber || supportNumber
  const clinicDeskEffectiveNowMs = clinicDeskNowMs + clinicDeskClockOffsetMs
  const screeningRoleView = resolveClinicDeskRoleView(
    clinicDeskData?.screeningDesk,
    clinicDeskData?.screener,
    clinicDeskEffectiveNowMs,
    'Screener',
  )
  const therapyRoleView = resolveClinicDeskRoleView(
    clinicDeskData?.therapyDesk,
    clinicDeskData?.therapist,
    clinicDeskEffectiveNowMs,
    'Therapist',
  )
  const useRecaptchaCaptcha = Boolean(
    clinicDeskData?.recaptchaEnabled && clinicDeskData?.recaptchaSiteKey,
  )
  const isCaptchaValid = useRecaptchaCaptcha
    ? Boolean(recaptchaToken)
    : captchaAnswer?.trim() === captchaChallenge?.answer

  async function submitPendingAction() {
    if (!pendingAction?.actionType || !clinicLocationId) {
      return
    }

    if (!isCaptchaValid) {
      toast.error('Please complete the verification challenge before continuing.')
      return
    }

    setSubmittingAction(true)

    try {
      const resp = await postCPDataWithoutParams('/api/secure/ca/doc-side/clinic-desk/contact', {
        clinicLocationId,
        actionType: pendingAction.actionType,
        recaptchaToken,
      })

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const actionLabel =
          pendingAction.actionType === 'support'
            ? 'Support has been notified.'
            : `${pendingAction.actionType === 'screening' ? 'Screener' : 'Therapist'} has been notified.`
        toast.success(actionLabel)

        if (pendingAction.actionType === 'support' && resolvedSupportNumber) {
          window.open(`tel:${resolvedSupportNumber}`, '_self')
        }

        resetActionDialog()
        return
      }

      toast.error(getResponseMessage(resp, 'Unable to process this request right now.'))
    } catch (err) {
      toast.error(getResponseMessage(err, 'Unable to process this request right now.'))
    } finally {
      setSubmittingAction(false)
      setRecaptchaToken('')
      recaptchaRef.current?.reset?.()
      setCaptchaChallenge(createFallbackCaptchaChallenge())
    }
  }

  const actionDialogTitle =
    pendingAction?.actionType === 'support'
      ? 'Call Human Support'
      : pendingAction?.actionType === 'screening'
        ? 'Call Screener'
        : 'Call Therapist'

  const actionDialogBody =
    pendingAction?.actionType === 'support'
      ? 'This will place a call to the human support number from your device and send an automated WhatsApp/email alert to support with the clinic assistant and clinic details.'
      : 'This will send an automated call, WhatsApp, and email alert to the assigned practitioner and the support team for this clinic.'

  return (
    <>
      {/* Floating Help Button - Hidden when drawer or modal is open, and behind other drawers */}
      {canShowDocSideButton && !drawerOpen && !modalOpen && (
        <Portal>
          <Button
            onClick={() => {
              setActiveTab(showClinicDeskTab ? clinicDeskTabIndex : 0)
              setHasAttemptedContentLoad(true)
              setDrawerOpen((prev) => !prev)
            }}
            size="large"
            className="hide-on-print-only"
            sx={{
              position: 'fixed',
              // For CA Portal: position in bottom half (25vh from bottom)
              // For other portals: position in top half (50vh + 120px from bottom)
              bottom: isCAPortal
                ? `calc(25vh - ${buttonTopOffset}px)`
                : `calc(50vh + 120px - ${buttonTopOffset}px)`,
              right: 0,
              bgcolor: 'emrPrimary.main',
              color: 'white',
              zIndex: 1100, // Lower than Material-UI Drawer default (1200) so it's behind drawers
              transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1)',
              '&:hover': { bgcolor: 'primary.dark' },
              writingMode: 'vertical-lr',
              textOrientation: 'mixed',
              transform: 'rotate(180deg) !important',
              padding: '16px 0px',
              borderRadius: '0px',
              minWidth: '0px !important',
            }}
            disableRipple
          >
            DocSide
          </Button>
        </Portal>
      )}

      {/* Drawer with Tabs */}
      {drawerOpen && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '100%', sm: 480, md: 640 },
              boxSizing: 'border-box',
              background:
                'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)',
            },
          }}
        >
            {/* Header */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 100%)',
                backdropFilter: 'blur(20px)',
                boxShadow: '0 4px 16px rgba(67, 56, 202, 0.2)',
                borderBottom: '1px solid rgba(67, 56, 202, 0.2)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '20px 24px',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: { xs: '20px', sm: '24px' },
                    letterSpacing: '0.5px',
                  }}
                >
                  DocSide
                </Typography>
                <IconButton
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              {/* Support Card */}
              {!!resolvedSupportNumber && (
                <Box sx={{ px: 3, pb: 2 }}>
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, fontSize: '0.875em', color: 'rgba(255, 255, 255, 0.9)' }}
                          >
                            थेरेपी बुक करने के लिए कृपया कॉल करें
                          </Typography>
                          <Typography
                            sx={{
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '1.25em',
                            }}
                          >
                            {resolvedSupportNumber &&
                              `${formatPhoneNumber(resolvedSupportNumber)?.formatType2Number}`}
                          </Typography>
                        </Box>
                        {showClinicDeskTab ? (
                          <Button
                            variant="contained"
                            onClick={() => openActionDialog('support')}
                            disabled={submittingAction || !resolvedSupportNumber}
                            sx={{
                              color: 'rgba(15, 23, 42, 0.95)',
                              backgroundColor: 'rgba(255, 255, 255, 0.96)',
                              minWidth: 132,
                              fontWeight: 700,
                              textTransform: 'none',
                              '&:hover': {
                                backgroundColor: 'white',
                              },
                            }}
                          >
                            {submittingAction && pendingAction?.actionType === 'support' ? (
                              <CircularProgress size={18} />
                            ) : (
                              'Call Support'
                            )}
                          </Button>
                        ) : (
                          <IconButton
                            component="a"
                            href={`${supportWhatsappLink}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: '#25D366',
                              backgroundColor: 'rgba(37, 211, 102, 0.15)',
                              '&:hover': {
                                backgroundColor: 'rgba(37, 211, 102, 0.25)',
                              },
                            }}
                          >
                            <WhatsAppIcon />
                          </IconButton>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'white',
                    height: 3,
                  },
                }}
              >
                {showClinicDeskTab && (
                  <Tab
                    label="Clinic Desk"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '15px',
                      '&.Mui-selected': {
                        color: 'white',
                      },
                    }}
                  />
                )}
                <Tab
                  label="Bulletin Board"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '15px',
                    '&.Mui-selected': {
                      color: 'white',
                    },
                  }}
                />
                <Tab
                  label="Help Videos"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    fontSize: '15px',
                    '&.Mui-selected': {
                      color: 'white',
                    },
                  }}
                />
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                padding: { xs: 2, sm: 3 },
              }}
            >
              {isLoadingContent ? (
                <Box
                  sx={{
                    minHeight: '320px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: 'rgba(100, 116, 139, 0.8)',
                  }}
                >
                  <Typography variant="body1">Loading help resources...</Typography>
                </Box>
              ) : (
                <>
              {showClinicDeskTab && activeTab === clinicDeskTabIndex && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 1,
                      color: 'rgba(67, 56, 202, 0.92)',
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    Clinic Desk
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: 'rgba(71, 85, 105, 0.82)',
                      lineHeight: 1.6,
                    }}
                  >
                    Trigger the automated screening, therapy, and support alert flows for this clinic desk.
                    The cards below show who is rostered right now, who is next up, or when nobody is
                    currently rostered for that role.
                  </Typography>

                  {isLoadingClinicDesk ? (
                    <Box
                      sx={{
                        minHeight: '240px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Grid2 container spacing={2.5}>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <ClinicDeskPractitionerCard
                          title="Screener"
                          description="Assigned screening practitioner"
                          practitioner={screeningRoleView.practitioner}
                          headline={screeningRoleView.headline}
                          buttonLabel={screeningRoleView.buttonLabel}
                          onClick={() =>
                            openActionDialog('screening', screeningRoleView.actionPractitioner)
                          }
                          busy={submittingAction && pendingAction?.actionType === 'screening'}
                          statusLabel={screeningRoleView.statusLabel}
                          statusTone={screeningRoleView.statusTone}
                          helperText={screeningRoleView.helperText}
                          timeLabel={screeningRoleView.timeLabel}
                          actionEnabled={screeningRoleView.actionEnabled}
                          showIdentity={screeningRoleView.showIdentity}
                          showButton={screeningRoleView.showButton}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12, sm: 6 }}>
                        <ClinicDeskPractitionerCard
                          title="Therapist"
                          description="Assigned therapy practitioner"
                          practitioner={therapyRoleView.practitioner}
                          headline={therapyRoleView.headline}
                          buttonLabel={therapyRoleView.buttonLabel}
                          onClick={() =>
                            openActionDialog('therapy', therapyRoleView.actionPractitioner)
                          }
                          busy={submittingAction && pendingAction?.actionType === 'therapy'}
                          statusLabel={therapyRoleView.statusLabel}
                          statusTone={therapyRoleView.statusTone}
                          helperText={therapyRoleView.helperText}
                          timeLabel={therapyRoleView.timeLabel}
                          actionEnabled={therapyRoleView.actionEnabled}
                          showIdentity={therapyRoleView.showIdentity}
                          showButton={therapyRoleView.showButton}
                        />
                      </Grid2>
                      <Grid2 size={{ xs: 12 }}>
                        <Card
                          elevation={0}
                          sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(67, 56, 202, 0.16)',
                            background:
                              'linear-gradient(135deg, rgba(239, 246, 255, 0.96) 0%, rgba(255, 255, 255, 0.98) 100%)',
                          }}
                        >
                          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                color: 'rgba(30, 41, 59, 0.94)',
                              }}
                            >
                              Human support fallback
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                mt: 1,
                                color: 'rgba(71, 85, 105, 0.82)',
                                lineHeight: 1.6,
                              }}
                            >
                              The support action places a direct call from this device and sends an automated
                              alert to the support number with clinic assistant details.
                            </Typography>
                            {!!resolvedSupportNumber && (
                              <Typography
                                sx={{
                                  mt: 1.5,
                                  fontWeight: 700,
                                  color: 'rgba(30, 41, 59, 0.96)',
                                  fontSize: '16px',
                                }}
                              >
                                {formatPhoneNumber(resolvedSupportNumber)?.formatType2Number ||
                                  resolvedSupportNumber}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid2>
                    </Grid2>
                  )}
                </Box>
              )}

              {/* Bulletin Board Tab */}
              {activeTab === bulletinTabIndex && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'rgba(67, 56, 202, 0.9)',
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    Bulletin Board
                  </Typography>
                  {bulletinContent.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        color: 'rgba(100, 116, 139, 0.7)',
                      }}
                    >
                      <Typography variant="body1">
                        No bulletins available at the moment.
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1, fontSize: '13px' }}>
                        Check back later for updates.
                      </Typography>
                    </Box>
                  ) : (
                    <Grid2 container spacing={2}>
                      {bulletinContent.map((item, idx) => (
                        <Grid2 size={{ xs: 12 }} key={idx}>
                          <Card
                            elevation={0}
                            sx={{
                              border: '1px solid rgba(67, 56, 202, 0.15)',
                              borderRadius: 2,
                              overflow: 'hidden',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(67, 56, 202, 0.15)',
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                {item.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {item.content}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid2>
                      ))}
                    </Grid2>
                  )}
                </Box>
              )}

              {/* Help Videos Tab */}
              {activeTab === helpVideosTabIndex && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: 'rgba(67, 56, 202, 0.9)',
                      fontWeight: 700,
                      fontSize: '20px',
                    }}
                  >
                    Help Videos
                  </Typography>
                  {playlistLinks?.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 8,
                        color: 'rgba(100, 116, 139, 0.7)',
                      }}
                    >
                      <Typography variant="body1">No videos available.</Typography>
                    </Box>
                  ) : (
                    <Grid2 container spacing={2}>
                      {playlistLinks?.map((link, idx) => {
                        if (link?.type === 'playlist' && link?.playlistVideoIds) {
                          return link.playlistVideoIds.map((video, videoIdx) => (
                            <Grid2 size={{ xs: 12, sm: 6 }} key={`${idx}-${videoIdx}`}>
                              <VideoThumbnailCard
                                video={video}
                                onClick={() => {
                                  setSelectedVideo(video)
                                  setModalOpen(true)
                                }}
                              />
                            </Grid2>
                          ))
                        } else if (link?.contentId) {
                          return (
                            <Grid2 size={{ xs: 12, sm: 6 }} key={idx}>
                              <VideoThumbnailCard
                                video={link}
                                onClick={() => {
                                  setSelectedVideo(link)
                                  setModalOpen(true)
                                }}
                              />
                            </Grid2>
                          )
                        }
                        return null
                      })}
                    </Grid2>
                  )}
                </Box>
              )}
                </>
              )}
            </Box>
        </Drawer>
      )}

      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => {
          if (!submittingAction) {
            resetActionDialog()
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{actionDialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(71, 85, 105, 0.84)',
              lineHeight: 1.7,
            }}
          >
            {actionDialogBody}
          </Typography>

          {pendingAction?.practitioner?.name && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(67, 56, 202, 0.14)',
                backgroundColor: 'rgba(248, 250, 252, 0.92)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(30, 41, 59, 0.95)' }}>
                Practitioner
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(51, 65, 85, 0.88)' }}>
                {pendingAction.practitioner.name}
              </Typography>
            </Box>
          )}

          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(148, 163, 184, 0.2)',
              backgroundColor: 'rgba(248, 250, 252, 0.92)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'rgba(30, 41, 59, 0.95)' }}>
              Verification
            </Typography>
            {useRecaptchaCaptcha ? (
              <Box sx={{ mt: 1.5 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={clinicDeskData?.recaptchaSiteKey}
                  onChange={(token) => setRecaptchaToken(token || '')}
                />
              </Box>
            ) : (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: 'rgba(71, 85, 105, 0.82)',
                  }}
                >
                  Please solve this simple captcha: <strong>{captchaChallenge?.prompt}</strong>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="text"
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                  placeholder="Enter the answer"
                  sx={{ mt: 1.5 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => resetActionDialog()} disabled={submittingAction}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => submitPendingAction()}
            disabled={!isCaptchaValid || submittingAction}
            sx={{ minWidth: 140, textTransform: 'none', fontWeight: 700 }}
          >
            {submittingAction ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Proceed'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Video Modal with Smooth Transition */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullScreen
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            m: 0,
            maxWidth: '100%',
            maxHeight: '100%',
          },
        }}
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 300,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(10px)',
          },
        }}
      >
        <Box
          position="relative"
          width="100%"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            padding: { xs: 1, sm: 2 },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '1600px',
              position: 'relative',
              borderRadius: { xs: 0, sm: 2 },
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              aspectRatio: '16/9',
              maxHeight: '90vh',
            }}
          >
            {selectedVideo && (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.contentId}?autoplay=1&rel=0`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.name || 'Help Video'}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => {
              setModalOpen(false)
              setSelectedVideo(null)
            }}
            sx={{
              position: 'absolute',
              top: { xs: 8, sm: 16 },
              right: { xs: 8, sm: 16 },
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              color: 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                bgcolor: 'white',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
              zIndex: 1300,
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedVideo?.name && (
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 8, sm: 16 },
                left: { xs: 8, sm: 16 },
                right: { xs: 8, sm: 16 },
                bgcolor: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                padding: 2,
                zIndex: 1300,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: { xs: '16px', sm: '20px' },
                }}
              >
                {selectedVideo.name}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  )
}
